import { purgeMerchandisingDrafts } from '../../merchandising/state/draftStorage';

/**
 * shellSessionStorage — short-lived shell session storage for the gateway JWT.
 *
 * Uses window.sessionStorage so data is automatically cleared when the browser
 * tab/window is closed. Only non-secret fields are persisted; raw credentials
 * (secret, access token from direct login) are never stored here.
 *
 * Stored fields:
 *   - gatewayAccessToken    JWT for gateway API calls
 *   - gatewayTokenExpiresAt unix seconds expiry of the access token
 *   - gatewayToken          installation token (used for routing)
 *   - gatewayBaseUrl        base URL of the gateway (e.g. https://gateway.pro.kealabs.com)
 *                           forwarded from plugin session response so the iframe can
 *                           build the gateway client without cross-origin parent access
 *   - routeShopId           shop id used in app routes
 *   - internalShopId        gateway-assigned canonical shop id
 *   - prestashopShopId      PrestaShop shop_id (from plugin context)
 *   - adminLanguageId       current authenticated admin language ID
 *   - platform              e.g. 'prestashop'
 *   - moduleVersion         installed Search Pro module version
 *   - domain                shop domain
 *   - storefrontUrl         full storefront URL from plugin context, used for preview links
 *   - capabilities          feature capability map (no secrets)
 *   - onboardingWizardCompleted  boolean|null — wizard done flag (null = unknown/fail-open)
 *
 * Expiry is checked on every load. If now >= gatewayTokenExpiresAt the entry
 * is cleared and null is returned.
 */

import { clearStoredTelemetryAuthority } from '../../telemetry/telemetryPolicy';

const STORAGE_KEY = 'kea.shell.session.v1';

const REQUIRED_FIELDS = [
  'gatewayAccessToken',
  'gatewayTokenExpiresAt',
  'gatewayToken',
  'routeShopId',
];

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Persist the non-secret fields from a ready shell bootstrap context.
 *
 * @param {object} context - Shell bootstrap context (status must be 'ready')
 */
export function saveShellSessionFromContext(context) {
  if (!context) return;

  const entry = {
    gatewayAccessToken: context.gatewayAccessToken ?? null,
    gatewayTokenExpiresAt: context.gatewayTokenExpiresAt ?? null,
    gatewayToken: context.gatewayToken ?? null,
    // Base URL for gateway API calls — forwarded from the plugin bridge so the
    // iframe can construct the gateway client without cross-origin parent access.
    gatewayBaseUrl: context.gatewayBaseUrl ?? null,
    routeShopId: context.routeShopId ?? null,
    internalShopId: context.internalShopId ?? null,
    prestashopShopId: context.prestashopShopId ?? null,
    adminLanguageId: context.adminLanguageId ?? null,
    platform: context.platform ?? null,
    moduleVersion: context.moduleVersion ?? null,
    domain: context.domain ?? null,
    storefrontUrl: context.storefrontUrl ?? null,
    // capabilities is a plain object with no sensitive data — safe to persist.
    capabilities: (typeof context.capabilities === 'object' && context.capabilities !== null)
      ? context.capabilities
      : {},
    // null = unknown/fail-open; false = wizard incomplete; true = wizard done.
    onboardingWizardCompleted: context.onboardingWizardCompleted ?? null,
    // Observability correlation identifier generated by ShellBootstrapEntry per run.
    // Optional — absent in sessions created before Task 10Q.
    flowId: context.flowId ?? null,
  };

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch (_e) {
    // sessionStorage unavailable (private mode quota exceeded, etc.) — silently ignore.
  }
}

/**
 * Load the shell session.
 *
 * Returns null when:
 *   - key is absent
 *   - value is invalid JSON
 *   - any required field is missing or null
 *   - the session is expired (now >= gatewayTokenExpiresAt)
 *
 * @param {number} [nowSec] - Override current time in unix seconds (for testing)
 * @returns {object|null}
 */
export function loadShellSession(nowSec) {
  let raw;
  try {
    raw = window.sessionStorage.getItem(STORAGE_KEY);
  } catch (_e) {
    return null;
  }

  if (!raw) return null;

  let entry;
  try {
    entry = JSON.parse(raw);
  } catch (_e) {
    return null;
  }

  if (!entry || typeof entry !== 'object') return null;

  // Validate required fields.
  if (!REQUIRED_FIELDS.every((field) => entry[field] != null)) return null;

  // Check expiry.
  if (isShellSessionExpired(entry, nowSec)) {
    clearShellSession();
    return null;
  }

  // Normalize onboardingWizardCompleted to a strict boolean or null.
  // Handles unexpected types written by corrupt or older session formats
  // (e.g. "true", "false", 1, 0) so the routing gate always gets a clean value.
  const rawCompleted = entry.onboardingWizardCompleted;
  if (rawCompleted === true || rawCompleted === 1 || rawCompleted === 'true') {
    entry.onboardingWizardCompleted = true;
  } else if (rawCompleted === false || rawCompleted === 0 || rawCompleted === 'false') {
    entry.onboardingWizardCompleted = false;
  } else {
    // Unknown / unexpected type → fail-open (null), never redirect to /setup.
    entry.onboardingWizardCompleted = null;
  }

  // flowId is optional — absent in older session snapshots; leave as-is.

  return entry;
}

/**
 * Mark that the user explicitly chose to skip the wizard ("Skip for now").
 *
 * Sets `wizardBypass = true` in the session so the wizard gate in App.js
 * does NOT redirect the user back to /setup when they navigate to /home.
 * Also records `onboardingWizardCompleted = false` so the "Setup incomplete"
 * banner is shown on the Home screen.
 *
 * Safe to call when no session is stored (no-op in that case, but the bypass
 * will not be persisted — the user simply won't be re-redirected because the
 * session also won't show `onboardingWizardCompleted === false`).
 */
export function setWizardBypass() {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const entry = JSON.parse(raw);
    if (!entry || typeof entry !== 'object') return;
    entry.wizardBypass = true;
    // Keep onboardingWizardCompleted = false so the Home banner is shown.
    if (entry.onboardingWizardCompleted !== true) {
      entry.onboardingWizardCompleted = false;
    }
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch (_e) {
    // sessionStorage unavailable — silently ignore.
  }
}

/**
 * Mark the onboarding wizard as completed in the current session entry.
 *
 * Called by SummaryStep after a successful `onboarding.complete` bridge call so
 * that subsequent navigation to /app/:shopId/home is not intercepted by the
 * wizard gate in App.js.
 *
 * Safe to call even when no session is stored (no-op in that case).
 */
export function markWizardCompleted() {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const entry = JSON.parse(raw);
    if (!entry || typeof entry !== 'object') return;
    entry.onboardingWizardCompleted = true;
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch (_e) {
    // sessionStorage unavailable — silently ignore.
  }
}

/**
 * Remove the shell session from sessionStorage.
 */
export function clearShellSession() {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
    clearStoredTelemetryAuthority();
    purgeMerchandisingDrafts();
  } catch (_e) {
    // Ignore.
  }
}

/**
 * Returns true if the session has expired relative to nowSec.
 *
 * @param {object} session - Session object with gatewayTokenExpiresAt (unix seconds)
 * @param {number} [nowSec] - Override current time in unix seconds (defaults to Date.now()/1000)
 * @returns {boolean}
 */
export function isShellSessionExpired(session, nowSec) {
  if (!session || session.gatewayTokenExpiresAt == null) return true;
  const now = nowSec !== undefined ? nowSec : Math.floor(Date.now() / 1000);
  return now >= session.gatewayTokenExpiresAt;
}
